import type { JSX } from 'react';
import { EXTERNAL_LINK_REL } from '../config/social-links';

interface FooterLinkProps {
  label: string;
  /** Absent while the destination has no content. */
  href?: string;
  /** Set for links leaving the site, which get `target` and `rel` treatment. */
  external?: boolean;
  className?: string;
}

/**
 * One footer entry — an anchor when there is somewhere to go, plain text when there is not.
 *
 * The five company and legal destinations exist in the design and have no content yet. Rendering
 * them as `<a href="#">`, which the design prototype does, would make them dead links: they jump
 * the page, they take keyboard focus for no reason, they get indexed, and link and accessibility
 * checkers flag them. Rendering them as text is the honest version — they are visibly present in
 * the layout, and nothing about them claims to be clickable.
 *
 * An `<a>` with `aria-disabled` was the other candidate and is worse: still focusable, still
 * navigable, and it announces a disabled control that nevertheless works.
 *
 * Giving one of them a page later means adding an `href` to its config entry. Nothing here
 * changes.
 */
export function FooterLink({ label, href, external, className }: FooterLinkProps): JSX.Element {
  if (!href) {
    return <span className={className}>{label}</span>;
  }

  return (
    <a
      className={className}
      href={href}
      {...(external
        ? // `noopener` denies the destination a `window.opener` handle back to this page and
          // `noreferrer` withholds the referrer. Browsers imply `noopener` for `target="_blank"`
          // now, but stating it keeps the guarantee independent of the browser.
          { target: '_blank', rel: EXTERNAL_LINK_REL }
        : {})}
    >
      {label}
    </a>
  );
}
